import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../services/supabaseClient';
import { User as SupabaseUser, Session } from '@supabase/supabase-js';

export type UserRole = 'agent_terrain' | 'agence' | 'soebra' | 'admin';

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  organization?: string;
  agent_code?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string, role: UserRole, organization?: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    
    // Vérifier la session existante
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      setSession(session);
      if (session) {
        fetchUserProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Écouter les changements d'authentification
    // Note: Ne pas appeler fetchUserProfile ici pour éviter les appels doubles
    // car login() le fait déjà directement
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!mounted) return;
      setSession(session);
      if (!session) {
        setUser(null);
        setLoading(false);
      }
      // Si session existe, ne pas appeler fetchUserProfile ici
      // car login() le fait déjà, et cela éviterait les appels doubles
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const fetchUserProfile = async (userId: string) => {
    try {
      console.log('fetchUserProfile appelé pour:', userId);
      
      // Essayer d'abord avec la fonction RPC qui contourne RLS
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_user_profile', {
        user_id: userId
      });

      if (!rpcError && rpcData && rpcData.length > 0) {
        console.log('Profil chargé via RPC avec succès:', rpcData[0]?.email);
        setUser(rpcData[0] as User);
        setLoading(false);
        return;
      }

      // Si RPC échoue, essayer avec la requête directe
      const { data, error } = await supabase
        .from('users')
        .select('id, email, name, role, organization, agent_code')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Erreur lors de la récupération du profil:', error);
        setUser(null);
        setLoading(false);
        return;
      }

      console.log('Profil chargé avec succès:', data?.email);
      setUser(data as User);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors de la récupération du profil:', error);
      setUser(null);
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      console.log('login appelé pour:', email);
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('Erreur signInWithPassword:', error);
        setLoading(false);
        throw error;
      }

      if (!data || !data.session || !data.user) {
        console.error('Aucune session créée');
        setLoading(false);
        throw new Error('Aucune session créée');
      }

      console.log('Session créée, chargement du profil...');
      // Définir la session d'abord
      setSession(data.session);
      // Attendre un peu pour que la session soit propagée dans Supabase
      await new Promise(resolve => setTimeout(resolve, 100));
      // Charger le profil
      await fetchUserProfile(data.user.id);
      console.log('Login terminé avec succès');
    } catch (error: any) {
      console.error('Erreur dans login:', error);
      setLoading(false);
      throw error;
    }
  };

  const signUp = async (
    email: string,
    password: string,
    name: string,
    role: UserRole,
    organization?: string
  ) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            role,
            organization: organization || null,
          },
        },
      });

      if (error) {
        throw error;
      }

      if (data.session && data.user) {
        setSession(data.session);
        await fetchUserProfile(data.user.id);
      }
    } catch (error: any) {
      console.error('Erreur lors de l\'inscription:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        login,
        signUp,
        logout,
        isAuthenticated: !!user && !!session,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
